/**
 * Privacy categories — the vocabulary of the visitor-facing privacy notice.
 *
 * Three categories only, in plain language:
 *   essential     always on, genuinely required to run the store
 *   analytics     optional measurement tied to a visitor
 *   advertising   optional ad measurement, personalisation, pixels, audiences
 *
 * These sit ALONGSIDE the messaging channels in `channels.ts` (whatsapp,
 * email, push) — a category governs tracking, a channel governs messages.
 */

/** Version of the privacy notice shown with the choice. Bump when copy changes. */
export const PRIVACY_NOTICE_VERSION = "2026-10-04";

export const OPTIONAL_CATEGORIES = ["analytics", "advertising"] as const;
export type OptionalCategory = (typeof OPTIONAL_CATEGORIES)[number];

export const PRIVACY_CATEGORIES = ["essential", ...OPTIONAL_CATEGORIES] as const;
export type PrivacyCategory = (typeof PRIVACY_CATEGORIES)[number];

export function isOptionalCategory(value: string): value is OptionalCategory {
  return (OPTIONAL_CATEGORIES as readonly string[]).includes(value);
}

/** Banner copy — exact wording approved for the privacy notice. */
export const PRIVACY_BANNER_TITLE = "We respect your privacy.";
export const PRIVACY_BANNER_BODY =
  "We use essential technologies to run our store. With your permission, we also use analytics to improve TruWe and advertising tools to show relevant offers. You can reject optional tracking and still shop normally. Change your choices anytime in Privacy settings.";

export const PRIVACY_ACTIONS = {
  acceptAll: "Accept all",
  rejectOptional: "Reject optional",
  chooseSettings: "Choose settings",
} as const;

export interface CategoryCopy {
  category: PrivacyCategory;
  label: string;
  description: string;
  /** Essential cannot be switched off — it is what makes the store work. */
  locked: boolean;
}

export const CATEGORY_COPY: Record<PrivacyCategory, CategoryCopy> = {
  essential: {
    category: "essential",
    label: "Essential",
    description:
      "Only what is genuinely needed to run the store: your cart, sign-in, checkout and payment, fraud and security protection, and remembering this privacy choice. Always on.",
    locked: true,
  },
  analytics: {
    category: "analytics",
    label: "Analytics",
    description:
      "Optional measurement tied to your visit — which pages and products are used, and where people get stuck — so we can improve TruWe. Off unless you allow it.",
    locked: false,
  },
  advertising: {
    category: "advertising",
    label: "Advertising",
    description:
      "Optional ad measurement, personalisation, advertising pixels and audience creation, so the offers you see are relevant. Off unless you allow it.",
    locked: false,
  },
};

export const PRIVACY_SETTINGS_LABEL = "Privacy settings";
